import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

/** BB5 (besluit en GO Frank 4 november 2026): wat je verwijderde staat 30 dagen in de prullenbak en kan terug.
 *
 *  GLASRONDE 0.9.243 PUNT 2 (Frank, 4 nov): "de prullenbak is op glas NIET te vinden". Gemeten: de route gaf 200 met
 *  een lege lijst, en dit blok toonde zich alleen ZODRA er iets in lag. Een functie die pas verschijnt als je haar
 *  nodig hebt, vind je op dat moment niet, want je weet niet dat ze bestaat. Daarom nu een KNOP in de kop van de
 *  pagina, altijd zichtbaar en ook bij een lege prullenbak, die een venster opent met de items, "Zet terug" en het
 *  aantal dagen tot ze echt weg zijn.
 *
 *  `soorten` is de lijst soorten die deze pagina toont ("geheugen", of "document" en "kennisbank"). `teller`
 *  verandert wanneer de pagina iets weggooit, zodat het aantal op de knop meteen bijwerkt. `onTerug` laat de pagina
 *  haar eigen lijst opnieuw lezen nadat iets terugstaat.
 */
const SOORTNAAM = { document: "Document", kennisbank: "Kennisbank" };

export default function PrullenbakBlok({ soorten, teller, onTerug, className = "" }) {
  const { t } = useTaal();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [bezig, setBezig] = useState(null);
  const sleutel = soorten.join(",");

  useEffect(() => {
    let weg = false;
    axios.get(`${API}/prullenbak`)
      .then((r) => { if (!weg) setItems((Array.isArray(r.data) ? r.data : []).filter((i) => soorten.includes(i.soort))); })
      .catch(() => { if (!weg) setItems([]); });
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleutel, teller, open]);

  const zetTerug = async (item) => {
    setBezig(item.id);
    try {
      await axios.post(`${API}/prullenbak/${item.id}/terug`);
      setItems((oud) => oud.filter((i) => i.id !== item.id));
      toast.success(t("Teruggezet: {naam}", { naam: item.naam }));
      if (onTerug) onTerug();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Terugzetten is mislukt"));
    } finally {
      setBezig(null);
    }
  };

  const naam = soorten.join("-");
  return (
    <>
      <Button variant="outline" size="sm" className={className} onClick={() => setOpen(true)}
              data-testid={`prullenbak-knop-${naam}`}>
        <Trash2 className="h-3 w-3 mr-1" />
        {t("Prullenbak")}{items.length ? ` (${items.length})` : ""}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`prullenbak-${naam}`}>
          <DialogHeader>
            <DialogTitle>{t("Prullenbak")}</DialogTitle>
            <DialogDescription>
              {t("Wat je verwijderde staat hier 30 dagen. Terugzetten zet het precies terug zoals het was.")}
            </DialogDescription>
          </DialogHeader>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="prullenbak-leeg">{t("De prullenbak is leeg.")}</p>
          ) : (
            <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
              {items.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-2 text-sm" data-testid="prullenbak-item">
                  <div className="min-w-0">
                    <p className="break-words">{item.naam}</p>
                    <p className="text-xs text-muted-foreground" data-testid={`prullenbak-dagen-${item.id}`}>
                      {SOORTNAAM[item.soort] ? `${t(SOORTNAAM[item.soort])} · ` : ""}
                      {item.dagen_over === 1 ? t("nog 1 dag") : t("nog {n} dagen", { n: item.dagen_over ?? 30 })}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" disabled={bezig === item.id} onClick={() => zetTerug(item)}
                          data-testid={`prullenbak-terug-${item.id}`}>
                    {t("Zet terug")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
